import { z } from 'zod';

import { isRealCivilDay } from '../utils/date';
import { STORED_ASSET_NAME_MAX_LENGTH, STORED_ASSET_NAME_RE } from './tenant';

// Measurements backbone (stats F1.2) — bulk write of samples.
// `unit` is an optional DISPLAY unit of the input (lbs, ml…) — BE converts it to
// the registry's canonical unit; omitted = the value is already canonical.
export const RecordMeasurementsDto = z.object({
  samples: z
    .array(
      z.object({
        metricKey: z
          .string()
          .min(1)
          .max(64)
          .regex(/^[a-z][a-z0-9_]*$/),
        value: z.number().finite(),
        unit: z.string().min(1).max(16).optional(),
        measuredAt: z.string().datetime({ offset: true }).optional(),
        // backfill via calendar — past dates allowed, future ones not (BE
        // checks). Shape alone is not enough: an impossible-but-past day
        // (2026-02-30) passes the lexicographic future check and reaches
        // Postgres as a 500.
        businessDate: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}$/)
          .refine(isRealCivilDay, { message: 'VALIDATION__InvalidDate' })
          .optional(),
        // 7.1 — what to do when the day already holds a value for this metric:
        // REPLACE (the default) takes the day, ADD adds to it (cumulative
        // metrics only — water, steps…). Either way the old value is kept in
        // the revision trail.
        onDuplicate: z.enum(['REPLACE', 'ADD']).optional(),
      }),
    )
    .min(1)
    .max(50),
  // Daily Activity: not persisted on the sample rows — `shareWithTrainer`
  // only fires a one-off notification to the client's trainer(s) on THIS
  // write; `period` only tags which reminder/entry surface it came from, for
  // the notification's deep-link. Self-entry only (recordForClient ignores
  // both — a trainer entering for a client can't "share with themself").
  shareWithTrainer: z.boolean().optional(),
  period: z.enum(['morning', 'evening']).optional(),
});
export type ApiRecordMeasurementsDto = z.infer<typeof RecordMeasurementsDto>;

// Goals (F1.3) — the trainee sets these for themself; max 3 ACTIVE (BE enforces)
export const CreateGoalDto = z.object({
  kind: z.enum(['METRIC_TARGET', 'WORKOUTS_PER_WEEK']),
  // required for METRIC_TARGET, forbidden for WORKOUTS_PER_WEEK (BE checks)
  metricKey: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/)
    .optional(),
  targetValue: z.number().finite().positive(),
  direction: z.enum(['AT_LEAST', 'AT_MOST']).default('AT_LEAST'),
  period: z.enum(['DAY', 'WEEK', 'MONTH']).default('DAY'),
  priority: z.number().int().min(0).max(10).default(0),
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  endDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});
export type ApiCreateGoalDto = z.infer<typeof CreateGoalDto>;

export const UpdateGoalDto = z.object({
  targetValue: z.number().finite().positive().optional(),
  direction: z.enum(['AT_LEAST', 'AT_MOST']).optional(),
  period: z.enum(['DAY', 'WEEK', 'MONTH']).optional(),
  priority: z.number().int().min(0).max(10).optional(),
  status: z.enum(['ACTIVE', 'ACHIEVED', 'ARCHIVED']).optional(),
  endDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
});
export type ApiUpdateGoalDto = z.infer<typeof UpdateGoalDto>;

// ——— F2: body and health ———

// Not just the shape: 2026-02-30 must be a 400 at the pipe, not a Postgres
// "out of range" 500 at the INSERT.
const dateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(isRealCivilDay, { message: 'VALIDATION__InvalidDate' });
const positiveMeasure = z.number().finite().positive();

// circumferences (cm) — at least one site entered
export const CircumferenceEntryDto = z
  .object({
    measuredAt: dateString.optional(),
    neck: positiveMeasure.optional(),
    shoulders: positiveMeasure.optional(),
    chest: positiveMeasure.optional(),
    waist: positiveMeasure.optional(),
    abdomen: positiveMeasure.optional(),
    hips: positiveMeasure.optional(),
    thigh: positiveMeasure.optional(),
    calf: positiveMeasure.optional(),
    biceps: positiveMeasure.optional(),
    forearm: positiveMeasure.optional(),
    note: z.string().max(2000).optional(),
  })
  .refine(
    (o) =>
      [
        o.neck,
        o.shoulders,
        o.chest,
        o.waist,
        o.abdomen,
        o.hips,
        o.thigh,
        o.calf,
        o.biceps,
        o.forearm,
      ].some((v) => v !== undefined),
    { message: 'VALIDATION__AtLeastOneSite' },
  );
export type ApiCircumferenceEntryDto = z.infer<typeof CircumferenceEntryDto>;

// skinfolds (mm) — at least one site entered
export const SkinfoldEntryDto = z
  .object({
    measuredAt: dateString.optional(),
    chest: positiveMeasure.optional(),
    midaxillary: positiveMeasure.optional(),
    triceps: positiveMeasure.optional(),
    subscapular: positiveMeasure.optional(),
    abdomen: positiveMeasure.optional(),
    suprailiac: positiveMeasure.optional(),
    thigh: positiveMeasure.optional(),
    biceps: positiveMeasure.optional(),
    calf: positiveMeasure.optional(),
    note: z.string().max(2000).optional(),
  })
  .refine(
    (o) =>
      [
        o.chest,
        o.midaxillary,
        o.triceps,
        o.subscapular,
        o.abdomen,
        o.suprailiac,
        o.thigh,
        o.biceps,
        o.calf,
      ].some((v) => v !== undefined),
    { message: 'VALIDATION__AtLeastOneSite' },
  );
export type ApiSkinfoldEntryDto = z.infer<typeof SkinfoldEntryDto>;

export const InBodyEntryDto = z.object({
  model: z.enum(['inbody_270', 'inbody_570', 'inbody_770']),
  measuredAt: dateString.optional(),
  muscleMassKg: positiveMeasure.optional(),
  bodyFatPct: positiveMeasure.optional(),
  bodyFatMassKg: positiveMeasure.optional(),
  visceralFat: positiveMeasure.optional(),
  totalBodyWater: positiveMeasure.optional(),
  intracellularWater: positiveMeasure.optional(),
  extracellularWater: positiveMeasure.optional(),
  bmr: positiveMeasure.optional(),
  proteinMass: positiveMeasure.optional(),
  boneMass: positiveMeasure.optional(),
  mineralMass: positiveMeasure.optional(),
  phaseAngle: positiveMeasure.optional(),
  segmental: z.record(z.string(), z.number().finite()).optional(),
  note: z.string().max(2000).optional(),
});
export type ApiInBodyEntryDto = z.infer<typeof InBodyEntryDto>;

const hrZonePair = z.number().int().min(20).max(250);
export const SetHrZonesDto = z
  .object({
    effectiveFrom: dateString.optional(),
    restingHr: hrZonePair.optional(),
    z1Min: hrZonePair,
    z1Max: hrZonePair,
    z2Min: hrZonePair,
    z2Max: hrZonePair,
    z3Min: hrZonePair,
    z3Max: hrZonePair,
    z4Min: hrZonePair,
    z4Max: hrZonePair,
    z5Min: hrZonePair,
    z5Max: hrZonePair,
  })
  .refine(
    (o) =>
      o.z1Min < o.z1Max &&
      o.z2Min < o.z2Max &&
      o.z3Min < o.z3Max &&
      o.z4Min < o.z4Max &&
      o.z5Min < o.z5Max,
    { message: 'VALIDATION__ZoneMinBelowMax' },
  )
  .refine(
    (o) =>
      o.z1Max <= o.z2Min &&
      o.z2Max <= o.z3Min &&
      o.z3Max <= o.z4Min &&
      o.z4Max <= o.z5Min,
    { message: 'VALIDATION__ZonesMustIncrease' },
  );
export type ApiSetHrZonesDto = z.infer<typeof SetHrZonesDto>;

export const SetTrainingLevelDto = z.object({
  level: z.enum(['NOT_FIT', 'ALMOST_FIT', 'FIT', 'PRO_FIT', 'ADVANCED_FIT']),
  effectiveFrom: dateString.optional(),
  note: z.string().max(2000).optional(),
});
export type ApiSetTrainingLevelDto = z.infer<typeof SetTrainingLevelDto>;

/**
 * What an exercise's reference max measures: 1RM for loaded exercises (kg),
 * max reps or max time (seconds) for the ones without a meaningful load.
 * An exercise is tracked with exactly one of them.
 */
export const REFERENCE_MAX_METRICS = [
  'one_rm',
  'max_reps',
  'max_time',
] as const;
export type ReferenceMaxMetric = (typeof REFERENCE_MAX_METRICS)[number];

// `unit` only applies to 1RM; max reps are a count and max time is seconds
export const CreateOneRmDto = z
  .object({
    exerciseId: z.string().uuid(),
    metric: z.enum(REFERENCE_MAX_METRICS).default('one_rm'),
    value: positiveMeasure,
    unit: z.enum(['kg', 'lbs']).default('kg'),
    measuredAt: dateString.optional(),
    note: z.string().max(2000).optional(),
  })
  .refine((dto) => dto.metric !== 'max_reps' || Number.isInteger(dto.value), {
    message: 'VALIDATION__MaxRepsMustBeWhole',
    path: ['value'],
  });
export type ApiCreateOneRmDto = z.infer<typeof CreateOneRmDto>;

/**
 * The filename a presign minted, on its way back in with the row.
 *
 * These two uploads used to mint the presign INSIDE the create and write the
 * row before the browser had PUT anything — so the server could never prove the
 * object existed, and a dropped connection left a row whose signed URL 404s
 * forever. They now follow the same order as every other upload in the product:
 * presign, PUT, then post the row carrying this name, which the handler proves
 * with `assertUploadLanded` before writing.
 *
 * A NAME, never a path: the server appends it to `<prefix>/`, so `../` or a
 * nested segment would address an object outside the caller's own prefix. Same
 * rule, and the same message, as the tenant branding columns.
 */
const UploadedAssetName = z
  .string()
  .min(1)
  .max(STORED_ASSET_NAME_MAX_LENGTH)
  .regex(STORED_ASSET_NAME_RE, 'VALIDATION__InvalidAssetName');

/**
 * The media types each stats upload accepts — exported so the PRESIGN can
 * enforce exactly what the row create enforces.
 *
 * They were only ever on the create DTOs, which was enough while the presign
 * lived inside the POST. Splitting it into its own GET moved the upload AHEAD
 * of them: a raw `?mediaType=text/html` minted `<uuid>.html` and signed a PUT
 * carrying that Content-Type into the private stats bucket, and the POST that
 * follows still refuses the row — so the object lands orphaned, with a type
 * nothing in the product accepts and no record that it exists.
 */
/**
 * The `errorMap` is not decoration. `ZodFilter` renders `messages[issue.message]`,
 * so a message that is not a catalog KEY becomes "Unknown, message could not be
 * translated" — and a bare `z.enum` produces zod's own prose ("Invalid enum
 * value. Expected 'image/jpeg' | …"), which is not one.
 *
 * That matters more here than on an ordinary field because these enums are the
 * WHOLE schema of a route (`GET …/signed-upload-url?mediaType=`), so the
 * placeholder is the entire answer, under an empty error key:
 * `{"errors":{"":"Unknown, message could not be translated"}}`. Measured live
 * against both stats presign routes.
 *
 * `VALIDATION__UnsupportedMediaType` already exists for exactly this refusal —
 * `getSignedUploadUrl` throws it when a media type cannot produce a storable
 * filename — so the two halves of "this media type is not allowed" now answer
 * with one sentence.
 */
const unsupportedMediaType = {
  errorMap: () => ({ message: 'VALIDATION__UnsupportedMediaType' }),
};

export const ProgressPhotoMediaType = z.enum(
  ['image/jpeg', 'image/jpg', 'image/png'],
  unsupportedMediaType,
);

export const InBodyFileMediaType = z.enum(
  ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'],
  unsupportedMediaType,
);

export const CreateProgressPhotoDto = z.object({
  takenAt: dateString.optional(),
  tag: z.string().max(64).optional(),
  // no value → default from UserSettings.photoVisibility (F6.1)
  visibility: z
    .enum(['PRIVATE', 'TRAINER', 'TRAINER_AND_ADMIN', 'PUBLIC'])
    .optional(),
  watermark: z.boolean().default(true),
  note: z.string().max(2000).optional(),
  mediaType: ProgressPhotoMediaType,
  mediaSource: UploadedAssetName,
});
export type ApiCreateProgressPhotoDto = z.infer<typeof CreateProgressPhotoDto>;

export const UpdateProgressPhotoDto = z.object({
  tag: z.string().max(64).nullable().optional(),
  visibility: z
    .enum(['PRIVATE', 'TRAINER', 'TRAINER_AND_ADMIN', 'PUBLIC'])
    .optional(),
  watermark: z.boolean().optional(),
  note: z.string().max(2000).nullable().optional(),
});
export type ApiUpdateProgressPhotoDto = z.infer<typeof UpdateProgressPhotoDto>;

export const AttachInBodyFileDto = z.object({
  fileType: z.enum(['pdf', 'photo']),
  mediaType: InBodyFileMediaType,
  originalName: z.string().max(255).optional(),
  // see `UploadedAssetName` above — presign, PUT, then post this
  mediaSource: UploadedAssetName,
});
export type ApiAttachInBodyFileDto = z.infer<typeof AttachInBodyFileDto>;

// ——— F3.3: Activity Log (free-form activities, C-08) ———
// Counting rules (R10 cmt 3, locked): counts toward workout count +
// effectiveness (activity level); NOT toward finances, NOT toward FS, NOT toward
// trainer-trainee accounting.
export const CreateActivityLogDto = z
  .object({
    type: z.enum([
      'cardio',
      'gym',
      'strength',
      'bodyweight',
      'stretching',
      'mobility',
      'yoga',
      'pilates',
      'sports_recreation',
      'custom',
    ]),
    customLabel: z.string().min(1).max(64).optional(),
    startedAt: z.string().datetime({ offset: true }).optional(),
    durationMin: z.number().finite().positive().optional(),
    kcal: z.number().int().positive().optional(),
    distanceM: z.number().finite().positive().optional(),
    note: z.string().max(2000).optional(),
  })
  .refine((o) => o.type !== 'custom' || !!o.customLabel, {
    message: 'VALIDATION__CustomActivityNeedsLabel',
  });
export type ApiCreateActivityLogDto = z.infer<typeof CreateActivityLogDto>;

// ——— F5: nutrition ———

// "+add nutrient" (item A, §A.8): custom nutrient per-tenant
export const CreateNutrientDto = z.object({
  key: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/),
  name: z.string().min(1).max(64),
  unit: z
    .enum(['g', 'mg', 'ug', 'kcal', 'kj', 'iu', 'ml', 'dl', 'l', 'cups'])
    .nullable()
    .optional(),
});
export type ApiCreateNutrientDto = z.infer<typeof CreateNutrientDto>;

// actual intake entry (E-02): catalog OR free-form name (exactly one)
export const CreateIntakeDto = z
  .object({
    type: z.enum(['FOOD', 'DRINK', 'SUPPLEMENT']),
    groceryId: z.string().uuid().optional(),
    freeName: z.string().min(1).max(128).optional(),
    quantity: z.number().finite().positive(),
    unit: z.enum(['g', 'mg', 'ml', 'tsp']).default('g'),
    consumedAt: z.string().datetime({ offset: true }).optional(),
    caffeineMg: z.number().finite().positive().optional(),
    note: z.string().max(2000).optional(),
  })
  .refine((o) => !!o.groceryId !== !!o.freeName, {
    message: 'VALIDATION__GroceryOrFreeName',
  });
export type ApiCreateIntakeDto = z.infer<typeof CreateIntakeDto>;

// F10.1 — FE self-report engagement actions (SAVE also happens automatically via
// the add-to-lib bridge; LIKE/SHARE/PREVIEW_CLICK are sent by FE)
export const CreateEngagementEventDto = z.object({
  contentType: z.enum([
    'exercise',
    'workout',
    'program',
    'meal',
    'nutrition_plan',
    'marketplace_item',
    'episode',
    'module',
    'course',
  ]),
  contentId: z.string().uuid(),
  action: z.enum(['SAVE', 'LIKE', 'SHARE', 'PREVIEW_CLICK']),
});
export type ApiCreateEngagementEventDto = z.infer<
  typeof CreateEngagementEventDto
>;

// F1.3 (FE) — stats privacy: write path for consent columns from F6.1
// (UserSettings); ALL fields optional, but at least one must be present
export const UpdateStatsPrivacyDto = z
  .object({
    allowTrainerViewAll: z.boolean().optional(),
    allowTrainerEditHealth: z.boolean().optional(),
    allowTrainerEditHrZones: z.boolean().optional(),
    allowStatsPreviewShare: z.boolean().optional(),
    photoVisibility: z
      .enum(['PRIVATE', 'TRAINER', 'TRAINER_AND_ADMIN', 'PUBLIC'])
      .optional(),
  })
  .refine((value) => Object.values(value).some((v) => v !== undefined), {
    message: 'VALIDATION__AtLeastOneField',
  });
export type ApiUpdateStatsPrivacyDto = z.infer<typeof UpdateStatsPrivacyDto>;
